import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Inbox, Users, Image as ImageIcon, MessageSquare, LogOut, Trash2, Eye, Plus, Save, Briefcase } from "lucide-react";
import ImageUploader from "../../components/admin/ImageUploader";
import SEO from "../../components/SEO";

function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("bcp_token");
    if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  return { user, loading };
}

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  if (loading) return <div className="min-h-screen bg-cream-light flex items-center justify-center"><span className="overline">Loading…</span></div>;
  if (!user) return <Navigate to="/admin/login" />;

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed", err);
    }
    localStorage.removeItem("bcp_token");
    delete api.defaults.headers.common["Authorization"];
    navigate("/admin/login");
  };

  const navItems = [
    { to: "/admin", icon: Inbox, label: "Submissions" },
    { to: "/admin/applications", icon: Briefcase, label: "Applications" },
    { to: "/admin/team", icon: Users, label: "Team" },
    { to: "/admin/projects", icon: ImageIcon, label: "Projects" },
    { to: "/admin/testimonials", icon: MessageSquare, label: "Testimonials" },
  ];

  return (
    <>
    <SEO title="Admin" description="" path="/admin" noindex />
    <div className="min-h-screen bg-cream-light flex">
      <aside className="w-64 bg-ink text-cream-light p-6 fixed inset-y-0">
        <Link to="/" className="flex items-center gap-3 mb-12">
          <div className="w-9 h-9 bg-cream-light text-ink flex items-center justify-center font-serif text-sm">BCP</div>
          <div>
            <div className="font-serif text-sm">BC PEABODY</div>
            <div className="text-[9px] tracking-[0.22em] opacity-60">ADMIN</div>
          </div>
        </Link>

        <nav className="space-y-1">
          {navItems.map((n) => {
            const active = loc.pathname === n.to;
            return (
              <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase()}`} className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${active ? "bg-copper text-cream-light" : "text-cream-light/70 hover:text-cream-light hover:bg-ink-mid"}`}>
                <n.icon size={16} /> {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="text-xs text-cream-light/50 mb-2">{user.email}</div>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-cream-light/70 hover:text-copper text-sm"><LogOut size={14}/> Logout</button>
        </div>
      </aside>

      <main className="ml-64 flex-1 p-10">
        <Routes>
          <Route index element={<Submissions />} />
          <Route path="applications" element={<Applications />} />
          <Route path="team" element={<TeamManager />} />
          <Route path="projects" element={<ProjectsManager />} />
          <Route path="testimonials" element={<TestimonialsManager />} />
        </Routes>
      </main>
    </div>
    </>
  );
}

function PageHead({ title, sub, action }) {
  return (
    <div className="mb-10 flex items-end justify-between">
      <div>
        <div className="overline mb-2">{sub}</div>
        <h1 className="font-serif text-5xl text-ink tracking-tight">{title}</h1>
      </div>
      {action}
    </div>
  );
}

function Submissions() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/submissions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.post(`/admin/submissions/${id}/read`); load(); };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/submissions/${id}`); load(); } };

  return (
    <div data-testid="admin-submissions">
      <PageHead title="Inbox" sub="Contact Form" />
      <div className="bg-cream border-t border-copper">
        {items.length === 0 && <div className="p-12 text-center text-ink/50">No messages yet.</div>}
        {items.map((s) => (
          <div key={s.id} className={`border-b border-ink/10 p-6 flex items-start justify-between gap-4 ${s.read ? "opacity-60" : ""}`}>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="font-serif text-xl text-ink">{s.first_name} {s.last_name}</h3>
                {!s.read && <span className="text-[9px] uppercase tracking-[0.2em] bg-copper text-cream-light px-2 py-0.5">New</span>}
                <span className="text-[10px] uppercase tracking-[0.2em] text-ink/50">{s.project_type}</span>
              </div>
              <div className="text-sm text-ink/60 mb-2">{s.email} · {new Date(s.submitted_at).toLocaleString()}</div>
              <p className="text-ink/80 text-sm whitespace-pre-wrap">{open === s.id ? s.message : s.message.slice(0, 140) + (s.message.length > 140 ? "…" : "")}</p>
              {s.message.length > 140 && (
                <button onClick={() => setOpen(open === s.id ? null : s.id)} className="text-copper text-xs uppercase tracking-[0.18em] mt-2">{open === s.id ? "Collapse" : "Read more"}</button>
              )}
            </div>
            <div className="flex gap-2">
              {!s.read && <button onClick={() => markRead(s.id)} className="p-2 text-ink/60 hover:text-copper" title="Mark read"><Eye size={16}/></button>}
              <button onClick={() => del(s.id)} className="p-2 text-ink/60 hover:text-red-600" title="Delete"><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Applications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/applications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.post(`/admin/applications/${id}/read`); load(); };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/applications/${id}`); load(); } };

  return (
    <div data-testid="admin-applications">
      <PageHead title="Applications" sub="Careers Inbox" />
      <div className="bg-cream border-t border-copper">
        {items.length === 0 && <div className="p-12 text-center text-ink/50">No applications yet.</div>}
        {items.map((s) => (
          <div key={s.id} className={`border-b border-ink/10 p-6 flex items-start justify-between gap-4 ${s.read ? "opacity-60" : ""}`}>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <h3 className="font-serif text-xl text-ink">{s.first_name} {s.last_name}</h3>
                {!s.read && <span className="text-[9px] uppercase tracking-[0.2em] bg-copper text-cream-light px-2 py-0.5">New</span>}
                <span className="text-[10px] uppercase tracking-[0.2em] text-ink/50">{s.role_interest}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{s.experience_years} yrs</span>
              </div>
              <div className="text-sm text-ink/60 mb-2">{s.email} {s.phone && `· ${s.phone}`} · {new Date(s.submitted_at).toLocaleString()}</div>
              {s.resume_url && <a href={s.resume_url} target="_blank" rel="noreferrer" className="text-copper text-xs uppercase tracking-[0.18em] mb-2 inline-block">View Resume →</a>}
              <p className="text-ink/80 text-sm whitespace-pre-wrap mt-2">{open === s.id ? s.message : s.message.slice(0, 140) + (s.message.length > 140 ? "…" : "")}</p>
              {s.message.length > 140 && (
                <button onClick={() => setOpen(open === s.id ? null : s.id)} className="text-copper text-xs uppercase tracking-[0.18em] mt-2">{open === s.id ? "Collapse" : "Read more"}</button>
              )}
            </div>
            <div className="flex gap-2">
              {!s.read && <button onClick={() => markRead(s.id)} className="p-2 text-ink/60 hover:text-copper" title="Mark read"><Eye size={16}/></button>}
              <button onClick={() => del(s.id)} className="p-2 text-ink/60 hover:text-red-600" title="Delete"><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CrudManager({ title, sub, endpoint, fields, idKey = "id" }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);

  const empty = () => fields.reduce((a, f) => ({ ...a, [f.name]: f.type === "list" ? [] : f.type === "bool" ? false : f.type === "number" ? 0 : "" }), {});

  const startNew = () => setEditing(empty());
  const startEdit = (it) => setEditing({ ...it });

  const save = async () => {
    const payload = { ...editing };
    if (Array.isArray(payload.attributes) && typeof payload.attributes === "string") payload.attributes = payload.attributes.split("\n").filter(Boolean);
    if (payload[idKey]) {
      await api.put(`/admin/${endpoint}/${payload[idKey]}`, payload);
    } else {
      await api.post(`/admin/${endpoint}`, payload);
    }
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${endpoint}/${id}`);
    load();
  };

  return (
    <div data-testid={`admin-${endpoint}`}>
      <PageHead title={title} sub={sub} action={
        <button onClick={startNew} data-testid={`admin-${endpoint}-new`} className="btn-copper !py-3 !px-5"><Plus size={14}/> New</button>
      } />

      {editing && (
        <div className="bg-cream border-t-2 border-copper p-8 mb-10">
          <h3 className="font-serif text-2xl mb-6">{editing[idKey] ? "Edit" : "New"} Item</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fields.map((f) => (
              <div key={f.name} className={f.full ? "md:col-span-2" : ""}>
                <label className="overline mb-2 block">{f.label}</label>
                {f.type === "image" ? (
                  <ImageUploader
                    value={editing[f.name] || ""}
                    folder={f.folder || "bcpeabody/uploads"}
                    onChange={(v) => setEditing({ ...editing, [f.name]: v })}
                  />
                ) : f.type === "image-list" ? (
                  <ImageUploader
                    value={editing[f.name] || []}
                    multiple
                    folder={f.folder || "bcpeabody/uploads"}
                    onChange={(v) => setEditing({ ...editing, [f.name]: v })}
                  />
                ) : f.type === "textarea" ? (
                  <textarea value={editing[f.name] || ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                    rows={4} className="w-full bg-cream-light border border-ink/15 px-4 py-3 outline-none focus:border-copper" />
                ) : f.type === "list" ? (
                  <textarea value={Array.isArray(editing[f.name]) ? editing[f.name].join("\n") : (editing[f.name] || "")}
                    onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value.split("\n") })}
                    rows={4} placeholder="One per line" className="w-full bg-cream-light border border-ink/15 px-4 py-3 outline-none focus:border-copper" />
                ) : f.type === "select" ? (
                  <select value={editing[f.name] || ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                    className="w-full bg-cream-light border border-ink/15 px-4 py-3 outline-none focus:border-copper">
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "bool" ? (
                  <label className="flex items-center gap-3"><input type="checkbox" checked={!!editing[f.name]} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.checked })} /> {f.label}</label>
                ) : f.type === "number" ? (
                  <input type="number" value={editing[f.name] || 0} onChange={(e) => setEditing({ ...editing, [f.name]: parseInt(e.target.value) || 0 })}
                    className="w-full bg-cream-light border border-ink/15 px-4 py-3 outline-none focus:border-copper" />
                ) : (
                  <input value={editing[f.name] || ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                    className="w-full bg-cream-light border border-ink/15 px-4 py-3 outline-none focus:border-copper" />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-8">
            <button onClick={save} data-testid={`admin-${endpoint}-save`} className="btn-copper"><Save size={14}/> Save</button>
            <button onClick={() => setEditing(null)} className="text-ink/60 text-sm uppercase tracking-[0.18em]">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((it) => (
          <div key={it[idKey]} className="bg-cream p-6 border-l-2 border-copper flex gap-4">
            {it.photo_url || it.image_url ? (
              <img src={it.photo_url || it.image_url} alt="" className="w-20 h-20 object-cover" />
            ) : null}
            <div className="flex-1">
              <h3 className="font-serif text-xl text-ink mb-1">{it.name || it.title || it.author}</h3>
              <p className="text-sm text-ink/60">{it.title || it.address || it.role || it.quote?.slice(0, 60)}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => startEdit(it)} className="text-copper text-xs uppercase tracking-[0.18em]">Edit</button>
              <button onClick={() => del(it[idKey])} className="text-red-600 text-xs uppercase tracking-[0.18em]">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamManager() {
  return <CrudManager title="Team Members" sub="Leadership & Field" endpoint="team" fields={[
    { name: "name", label: "Name" },
    { name: "title", label: "Title" },
    { name: "category", label: "Category", type: "select", options: ["leadership", "field"] },
    { name: "photo_url", label: "Photo", type: "image", folder: "bcpeabody/team", full: true },
    { name: "photo_position", label: "Photo Position (e.g. 'center', '30% top', '50% 25%')" },
    { name: "is_placeholder_photo", label: "This is a temporary stock portrait (show 'Portrait placeholder' badge)", type: "bool" },
    { name: "quote", label: "Quote", type: "textarea", full: true },
    { name: "bio", label: "Bio", type: "textarea", full: true },
    { name: "outside_work", label: "Outside Work (short personal detail, e.g. 'Boston sports, golf, tennis'). Optional." },
    { name: "attributes", label: "Attributes (one per line)", type: "list", full: true },
    { name: "active", label: "Active (uncheck to hide this person from the public Team page)", type: "bool" },
    { name: "order", label: "Order", type: "number" },
  ]} />;
}

function ProjectsManager() {
  return <CrudManager title="Projects" sub="Portfolio" endpoint="projects" fields={[
    { name: "title", label: "Title" },
    { name: "slug", label: "Slug (url-friendly, e.g. my-project)" },
    { name: "address", label: "Address" },
    { name: "neighborhood", label: "Neighborhood" },
    { name: "category", label: "Category", type: "select", options: ["custom-home", "renovation", "commercial", "federal", "disaster", "public-infrastructure", "federal-construction", "federal-consulting", "construction-claims", "founding-experience"] },
    { name: "year", label: "Year" },
    { name: "year_built", label: "Year Built / Detail" },
    { name: "beds", label: "Beds" },
    { name: "baths", label: "Baths" },
    { name: "sqft", label: "Sq Ft" },
    { name: "image_url", label: "Hero Image", type: "image", folder: "bcpeabody/projects", full: true },
    { name: "description", label: "Short Description", type: "textarea", full: true },
    { name: "long_description", label: "Long Description", type: "textarea", full: true },
    { name: "gallery", label: "Gallery (After photos)", type: "image-list", folder: "bcpeabody/projects", full: true },
    { name: "before_images", label: "Before Photos", type: "image-list", folder: "bcpeabody/projects", full: true },
    { name: "key_features", label: "Key Features (one per line)", type: "list", full: true },
    { name: "testimonial_quote", label: "Owner Testimonial Quote", type: "textarea", full: true },
    { name: "testimonial_author", label: "Testimonial Author (e.g. Waverly Park Homeowner)" },
    { name: "testimonial_role", label: "Testimonial Role (e.g. Homeowner, South Tampa)" },
    { name: "role_attribution", label: "Role Attribution", type: "select", options: ["firm", "consulting", "founding-team"] },
    { name: "featured", label: "Featured (show on homepage)", type: "bool" },
    { name: "order", label: "Order", type: "number" },
  ]} />;
}

function TestimonialsManager() {
  return <CrudManager title="Testimonials" sub="Client Voices" endpoint="testimonials" fields={[
    { name: "author", label: "Author" },
    { name: "role", label: "Role" },
    { name: "quote", label: "Quote", type: "textarea", full: true },
    { name: "order", label: "Order", type: "number" },
  ]} />;
}
